import React, { useState, useEffect, useCallback } from 'react';
import { Users, Shield, ShieldAlert, ShieldCheck, UserPlus, Edit2, Trash2, X, AlertCircle, Loader2, Search, Check, Ban, Key, User as UserIcon, Clock, MapPin, Eye, EyeOff, HeartPulse } from 'lucide-react';
import { format } from 'date-fns';
import authService from '../services/auth';
import cabangService from '../services/cabang';
import bagianService from '../services/bagian';
import PaginationBar from './PaginationBar';
import usePagination from '../hooks/usePagination';

const ROLES = [
  { value: 'admin', label: 'Admin', icon: ShieldAlert, color: 'from-red-500 to-rose-600', bg: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'user', label: 'User', icon: Shield, color: 'from-blue-500 to-indigo-600', bg: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'viewer', label: 'Viewer', icon: ShieldCheck, color: 'from-green-500 to-emerald-600', bg: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'perawat', label: 'Perawat', icon: HeartPulse, color: 'from-purple-500 to-pink-600', bg: 'bg-purple-100 text-purple-700 border-purple-200' },
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);

  // filteredUsers harus sebelum usePagination untuk hindari Temporal Dead Zone
  const filteredUsers = React.useMemo(() => {
    if (!users.length) return [];
    return users.filter(user => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        user.username.toLowerCase().includes(term) ||
        (user.name && user.name.toLowerCase().includes(term)) ||
        user.role.toLowerCase().includes(term) ||
        (user.bagian && user.bagian.toLowerCase().includes(term))
      );
    });
  }, [users, searchTerm]);

  const { paginatedItems, currentPage, totalPages, totalItems, startIndex, endIndex, goToPage } = usePagination(filteredUsers, 10);
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [cabangList, setCabangList] = useState([]);
  const [bagianList, setBagianList] = useState([]);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    role: 'user',
    bagian: 'Umum',
    cabangId: '',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.getUsers();
      setUsers(response.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    cabangService.getCabang().then(res => setCabangList(res.cabang || [])).catch(() => {});
    bagianService.getBagian().then(res => setBagianList(res.bagian || [])).catch(() => {});
  }, [fetchUsers]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const getRoleConfig = (role) => {
    return ROLES.find(r => r.value === role) || ROLES[1];
  };

  const resetForm = () => {
    setFormData({ username: '', password: '', name: '', role: 'user', bagian: 'Umum', cabangId: '' });
    setFormError('');
  };

  // Create user
  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.username.trim()) {
      setFormError('Username is required');
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    setFormLoading(true);
    try {
      await authService.register(formData.username.trim(), formData.password, formData.role, formData.name.trim(), formData.bagian, formData.cabangId || null);
      await fetchUsers();
      setShowCreateModal(false);
      resetForm();
      showSuccess('User created successfully');
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Edit user
  const handleEditClick = (user) => {
    setEditingUser(user);
    const existingCabangId = typeof user.cabang === 'object' ? (user.cabang?._id || '') : (user.cabang || '');
    setFormData({
      username: user.username,
      name: user.name || '',
      role: user.role,
      bagian: user.bagian || 'Umum',
      cabangId: existingCabangId,
      password: '',
    });
    setFormError('');
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const updateData = {};
    if (formData.role !== editingUser.role) updateData.role = formData.role;
    if (formData.name !== (editingUser.name || '')) updateData.name = formData.name.trim();
    if (formData.bagian !== (editingUser.bagian || 'Umum')) updateData.bagian = formData.bagian;
    const existingCabangId = typeof editingUser.cabang === 'object' ? (editingUser.cabang?._id || '') : (editingUser.cabang || '');
    if (formData.cabangId !== existingCabangId) updateData.cabangId = formData.cabangId || null;
    if (formData.password) updateData.password = formData.password;

    if (Object.keys(updateData).length === 0) {
      setShowEditModal(false);
      setEditingUser(null);
      resetForm();
      return;
    }

    setFormLoading(true);
    try {
      await authService.updateUser(editingUser._id, updateData);
      await fetchUsers();
      setShowEditModal(false);
      setEditingUser(null);
      resetForm();
      showSuccess('User updated successfully');
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (user) => {
    try {
      await authService.updateUser(user._id, { isActive: !user.isActive });
      await fetchUsers();
      showSuccess(user.isActive ? 'User disabled' : 'User activated');
    } catch (err) {
      setError(err.message);
    }
  };

  // Edit password
  const handleEditPassword = async () => {
    if (!showPasswordModal) return;
    if (!newPassword || newPassword.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }
    try {
      setFormLoading(true);
      setFormError('');
      await authService.updateUser(showPasswordModal._id, { password: newPassword });
      setShowPasswordModal(null);
      setNewPassword('');
      showSuccess(`Password for @${showPasswordModal.username} has been changed successfully`);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Delete user
  const handleDelete = async (id) => {
    try {
      setFormLoading(true);
      await authService.deleteUser(id);
      await fetchUsers();
      setShowDeleteConfirm(null);
      showSuccess('User deleted successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage users, roles, and access permissions
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="btn-primary text-sm"
        >
          <UserPlus className="w-4 h-4" />
          <span>Add User</span>
        </button>
      </div>

      {/* Success Alert */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 shadow-sm animate-slide-up">
          <div className="flex items-center space-x-2">
            <Check className="w-5 h-5 text-green-500" />
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm animate-slide-up">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-1 text-sm font-medium text-red-600 hover:text-red-500 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card">
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search users by name, username, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="relative">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
            <div className="absolute inset-0 h-10 w-10 rounded-full border-4 border-blue-100 animate-ping opacity-20" />
          </div>
          <span className="mt-4 text-gray-500 font-medium">Loading users...</span>
        </div>
      ) : (
        <>
          {/* Users Table */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Bagian</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cabang</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Login</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                  {paginatedItems.length > 0 ? (
                    paginatedItems.map((user, idx) => {
                      const roleConfig = getRoleConfig(user.role);
                      const RoleIcon = roleConfig.icon;
                      return (
                        <tr
                          key={user._id}
                          className="hover:bg-gray-50 transition-colors"
                          style={{ animationDelay: `${idx * 20}ms` }}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-3">
                              <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br ${roleConfig.color} text-white`}>
                                <UserIcon className="w-4 h-4" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">
                                  {user.name || user.username}
                                </div>
                                <div className="text-xs text-gray-400 truncate max-w-[200px]">
                                  @{user.username}
                                </div>
                              </div>                              </div>
                            </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-700 border-gray-200">
                              {user.bagian || 'Umum'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {user.cabang ? (
                              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                                <MapPin className="w-3 h-3 mr-1" />
                                {typeof user.cabang === 'object' ? user.cabang?.name : (cabangList.find(c => c._id === user.cabang)?.name || '—')}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400 italic">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${roleConfig.bg}`}>
                              <RoleIcon className="w-3 h-3 mr-1" />
                              {roleConfig.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {user.isActive !== false ? (
                              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 border border-green-200">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700 border border-red-200">
                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5" />
                                Disabled
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {user.lastLogin ? (
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3.5 h-3.5 text-gray-400" />
                                <span>{format(new Date(user.lastLogin), 'MMM dd, HH:mm')}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Never</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              <span>{format(new Date(user.createdAt), 'MMM dd, yyyy')}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-1">
                              <button
                                onClick={() => setViewDetail(user)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                title="Lihat detail"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditClick(user)}
                                className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                title="Edit user"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleToggleActive(user)}
                                className={`p-2 rounded-lg transition-all ${
                                  user.isActive !== false
                                    ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                                    : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                                }`}
                                title={user.isActive !== false ? 'Disable user' : 'Enable user'}
                              >
                                {user.isActive !== false ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => { setShowPasswordModal(user); setNewPassword(''); }}
                                className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                                title="Edit password"
                              >
                                <Key className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setShowDeleteConfirm(user)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
                            <Users className="w-7 h-7 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium">No users found</p>
                          <p className="text-gray-400 text-sm mt-1">
                            {searchTerm ? 'Try adjusting your search' : 'Add a user to get started'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationBar currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} goToPage={goToPage} />
          </div>
        </>
      )}

      {/* View Detail Modal */}
      {viewDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => setViewDetail(null)}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-auto z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-br ${getRoleConfig(viewDetail.role).color} text-white`}>
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Detail User</h3>
                    <p className="text-sm text-gray-500">Informasi lengkap user</p>
                  </div>
                </div>
                <button onClick={() => setViewDetail(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="flex items-center space-x-4 pb-4 border-b border-gray-100">
                  <div className={`flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br ${getRoleConfig(viewDetail.role).color} flex items-center justify-center`}>
                    <UserIcon className="w-7 h-7 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xl font-bold text-gray-900 truncate">{viewDetail.name || viewDetail.username}</h4>
                    <p className="text-sm text-gray-500">@{viewDetail.username}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getRoleConfig(viewDetail.role).bg}`}>
                        {React.createElement(getRoleConfig(viewDetail.role).icon, { className: 'w-3 h-3 mr-1' })}
                        {getRoleConfig(viewDetail.role).label}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${viewDetail.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${viewDetail.isActive !== false ? 'bg-green-500' : 'bg-red-500'}`} />
                        {viewDetail.isActive !== false ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Bagian</label>
                    <p className="text-sm font-semibold text-gray-900">{viewDetail.bagian || 'Umum'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Cabang</label>
                    <p className="text-sm font-semibold text-gray-900">
                      {viewDetail.cabang ? (
                        typeof viewDetail.cabang === 'object' ? viewDetail.cabang?.name : (cabangList.find(c => c._id === viewDetail.cabang)?.name || '-')
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Terakhir Login</label>
                    <p className="text-sm font-semibold text-gray-900">
                      {viewDetail.lastLogin ? format(new Date(viewDetail.lastLogin), 'MMM dd, HH:mm') : <span className="text-gray-400 italic">Never</span>}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Dibuat pada</label>
                    <p className="text-sm font-semibold text-gray-900">
                      {viewDetail.createdAt ? format(new Date(viewDetail.createdAt), 'MMM dd, yyyy') : '-'}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button onClick={() => setViewDetail(null)} className="btn-secondary text-sm">
                    <X className="w-4 h-4" /><span>Tutup</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => setShowCreateModal(false)}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
            <div
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto z-10 animate-scale-in"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Create User</h3>
                    <p className="text-sm text-gray-500">Add a new user to the system</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreate} className="px-6 py-5 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{formError}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                      placeholder="e.g. johndoe"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                      placeholder="e.g. John Doe"
                    />
                  </div>
                </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Bagian <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.bagian}
                  onChange={(e) => setFormData(prev => ({ ...prev, bagian: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                >
                  {bagianList.map(b => (
                    <option key={b._id} value={b.name}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Cabang
                </label>
                <select
                  value={formData.cabangId}
                  onChange={(e) => setFormData(prev => ({ ...prev, cabangId: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                >
                  <option value="">— Pilih Cabang —</option>
                  {cabangList.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">User hanya bisa melihat data cabang ini</p>
              </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Key className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                      placeholder="Minimum 6 characters"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {ROLES.map(role => {
                      const RoleIcon = role.icon;
                      const isSelected = formData.role === role.value;
                      return (
                        <button
                          key={role.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, role: role.value }))}
                          className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className={`p-1.5 rounded-lg bg-gradient-to-br ${role.color} text-white mb-1`}>
                            <RoleIcon className="w-4 h-4" />
                          </div>
                          <span className="text-xs font-semibold text-gray-700">{role.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading}
                    className="btn-primary text-sm"
                  >
                    {formLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /><span>Creating...</span></>
                    ) : (
                      <><UserPlus className="w-4 h-4" /><span>Create User</span></>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => { setShowEditModal(false); setEditingUser(null); resetForm(); }}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
            <div
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto z-10 animate-scale-in"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
                    <Edit2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Edit User</h3>
                    <p className="text-sm text-gray-500">@{editingUser.username}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowEditModal(false); setEditingUser(null); resetForm(); }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleEditSubmit} className="px-6 py-5 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{formError}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Username
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    disabled
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-400 mt-1">Username cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                    placeholder="Full name"
                  />
                </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Bagian <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.bagian}
                  onChange={(e) => setFormData(prev => ({ ...prev, bagian: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                >
                  {bagianList.map(b => (
                    <option key={b._id} value={b.name}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Cabang
                </label>
                <select
                  value={formData.cabangId}
                  onChange={(e) => setFormData(prev => ({ ...prev, cabangId: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                >
                  <option value="">— Pilih Cabang —</option>
                  {cabangList.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">User hanya bisa melihat data cabang ini</p>
              </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {ROLES.map(role => {
                      const RoleIcon = role.icon;
                      const isSelected = formData.role === role.value;
                      return (
                        <button
                          key={role.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, role: role.value }))}
                          className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className={`p-1.5 rounded-lg bg-gradient-to-br ${role.color} text-white mb-1`}>
                            <RoleIcon className="w-4 h-4" />
                          </div>
                          <span className="text-xs font-semibold text-gray-700">{role.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Password */}
                <div className="pt-2 border-t border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    New Password
                  </label>
                  <p className="text-xs text-gray-400 mb-2">Kosongkan jika tidak ingin mengubah password</p>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Key className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                      placeholder="Minimum 6 characters"
                    />
                  </div>
                  {formData.password && formData.password.length > 0 && formData.password.length < 6 && (
                    <p className="text-xs text-red-500 mt-1">Password must be at least 6 characters</p>
                  )}
                  {formData.password && formData.password.length >= 6 && (
                    <p className="text-xs text-green-600 mt-1">✓ Password akan diubah saat Save Changes diklik</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowEditModal(false); setEditingUser(null); resetForm(); }}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading}
                    className="btn-primary text-sm"
                  >
                    {formLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></>
                    ) : (
                      <><Check className="w-4 h-4" /><span>Save Changes</span></>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => { setShowPasswordModal(null); setNewPassword(''); setFormError(''); }}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
            <div
              className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-auto z-10 animate-scale-in"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 text-white">
                    <Key className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Edit Password</h3>
                    <p className="text-sm text-gray-500">@{showPasswordModal.username}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowPasswordModal(null); setNewPassword(''); setFormError(''); }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{formError}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Key className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                      placeholder="Minimum 6 characters"
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Masukkan password baru untuk @{showPasswordModal.username}
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowPasswordModal(null); setNewPassword(''); setFormError(''); }}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditPassword}
                    disabled={formLoading}
                    className="px-4 py-2.5 bg-orange-600 text-white font-semibold rounded-xl hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all text-sm disabled:opacity-50 flex items-center space-x-2"
                  >
                    {formLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></>
                    ) : (
                      <><Key className="w-4 h-4" /><span>Save Password</span></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => setShowDeleteConfirm(null)}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
            <div
              className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-auto z-10 animate-scale-in"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-6 py-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Delete User</h3>
                <p className="text-sm text-gray-500 mb-1">
                  Are you sure you want to delete
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  @{showDeleteConfirm.username}?
                </p>
                <p className="text-xs text-red-500 mt-2">This action cannot be undone.</p>

                <div className="flex justify-center space-x-3 mt-6">
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(showDeleteConfirm._id)}
                    disabled={formLoading}
                    className="px-4 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all text-sm disabled:opacity-50 flex items-center space-x-2"
                  >
                    {formLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /><span>Deleting...</span></>
                    ) : (
                      <><Trash2 className="w-4 h-4" /><span>Delete User</span></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
